import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  element: React.ReactNode;
  allowedRoles?: ('Owner' | 'Manager' | 'Staff')[];
  platformAdminOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  element,
  allowedRoles,
  platformAdminOnly,
}) => {
  const { accessToken, user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If there's no access token, we can immediately stop loading and redirect
    if (!accessToken) {
      setLoading(false);
      setError(null);
      return;
    }

    // If we have a token but no user yet, we're still loading user data
    if (accessToken && user === null) {
      setLoading(true);
      setError(null);
    } else {
      // We have both token and user data (or token is invalid and user fetch failed)
      setLoading(false);
      setError(null);
    }
  }, [accessToken, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-900 dark:text-white">
        Loading...
      </div>
    ); // Replace with a spinner if you have one
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!accessToken) {
    return (
      <Navigate
        to="/signin"
        replace
        state={{
          from: location.pathname,
          message: 'Please sign in to access this page.',
        }}
      />
    );
  }

  // Superuser bypass (optional)
  if (user && user.email === 'sp@askarthur.io') {
    return <>{element}</>;
  }

  // Platform admin only protection
  if (platformAdminOnly && (!user || !user.is_platform_admin)) {
    return <Navigate to="/" />;
  }

  // Role-based protection
  if (
    allowedRoles &&
    (!user || !user.role || !allowedRoles.includes(user.role))
  ) {
    return <Navigate to="/" />;
  }

  return <>{element}</>;
};

export default ProtectedRoute;
